'use client'

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import dynamic from 'next/dynamic'
import { useNavbarExtras } from '@/context/NavbarExtrasContext'
import { trackPageView, trackViewItemList } from '@/lib/timed-ui/analytics'
import EventCard from '@/components/timed-ui/events/EventCard'
import SeriesCard from '@/components/timed-ui/events/SeriesCard'
import EventListItem from '@/components/timed-ui/events/EventListItem'
import SeriesListItem from '@/components/timed-ui/events/SeriesListItem'
import EventCardSkeleton from '@/components/timed-ui/common/EventCardSkeleton'
import LoadingSpinner from '@/components/timed-ui/common/LoadingSpinner'
import { groupEventsBySeries } from '@/lib/timed-ui/event-helpers'
import { adaptEventsListForUI, type TimedUIEvent } from '@/lib/timed-ui/adapters'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import { getTemplateConfig } from '@/lib/templates/template-config'
import VenueMapViewer from '@/components/timed-ui/common/VenueMapViewer'
import LocationGroupView from '@/components/timed-ui/events/LocationGroupView'

const EventMap = dynamic(() => import('@/components/timed-ui/events/EventMap'), {
  ssr: false,
  loading: () => (
    <div className="bg-surface border border-surface-border rounded-xl p-12 flex items-center justify-center map-container">
      <LoadingSpinner text="Loading map..." />
    </div>
  ),
})

const VIEW_MODES = ['cards', 'list', 'locations', 'map'] as const
type ViewMode = typeof VIEW_MODES[number]

const VIEW_ICONS: Record<ViewMode, { title: string; path: React.ReactNode }> = {
  cards: {
    title: 'Card view',
    path: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />,
  },
  list: {
    title: 'List view',
    path: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />,
  },
  locations: {
    title: 'Location view',
    path: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></>,
  },
  map: {
    title: 'Map view',
    path: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />,
  },
}

const ViewToggle = ({ viewMode, onChange, availableViews }: { viewMode: ViewMode; onChange: (mode: ViewMode) => void; availableViews: ViewMode[] }) => {
  const btnBase = 'p-2.5 sm:p-2 transition-colors touch-manipulation'
  const btnActive = 'bg-primary text-text-inverse'
  const btnInactive = 'text-text-tertiary hover:text-text-primary hover:bg-surface-secondary'

  // Don't render the toggle if only one view is available
  if (availableViews.length <= 1) return null

  return (
    <div className="inline-flex rounded-lg border border-surface-border overflow-hidden bg-surface shadow-sm">
      {availableViews.map((mode, i) => {
        const icon = VIEW_ICONS[mode]
        return (
          <button
            key={mode}
            onClick={() => onChange(mode)}
            className={`${btnBase} ${i > 0 ? 'border-l border-surface-border ' : ''}${viewMode === mode ? btnActive : btnInactive}`}
            title={icon.title}
            aria-label={icon.title}
            aria-pressed={viewMode === mode}
          >
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {icon.path}
            </svg>
          </button>
        )
      })}
    </div>
  )
}

export default function PublicHomePage() {
  const { branding } = useTheme()
  const template = getTemplateConfig(branding?.layout)
  // Feature flags (branding.publicSite.features). Absent/true => show (current
  // behavior); explicit false => hide.
  const features = (branding?.publicSite as { features?: Record<string, unknown> } | undefined)?.features || {}
  const showEventSearch = features.showEventSearch !== false
  const showUpcomingEvents = features.showUpcomingEvents !== false
  const searchParams = useSearchParams()
  const router = useRouter()
  const initialQuery = searchParams.get('q') || ''
  const initialLocation = searchParams.get('location') || ''
  const [events, setEvents] = useState<TimedUIEvent[]>([])
  const [seasons, setSeasons] = useState<Array<{
    id: string; name: string; slug: string; description: string | null
    startDate: string; endDate: string; imageUrl: string | null; bannerUrl: string | null
    venue: { name: string; city: string; state: string }
    eventCount: number; startingAt: number | null
    ticketTypes: Array<{ id: string; name: string; price: number; available: number }>
  }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState(initialQuery)
  const [activeSearch, setActiveSearch] = useState(initialQuery)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [locationFilter, setLocationFilter] = useState(initialLocation)
  const [groupByVenue, setGroupByVenue] = useState(false)
  const availableViews = template.homepage.availableViews as ViewMode[]
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const fallback = availableViews.includes(template.homepage.defaultView as ViewMode)
      ? template.homepage.defaultView as ViewMode
      : availableViews[0] || 'cards'
    if (typeof window === 'undefined') return fallback
    const saved = localStorage.getItem('eventViewMode') as ViewMode | null
    return saved && availableViews.includes(saved) ? saved : fallback
  })
  const userChangedViewRef = useRef(false)

  // Sync viewMode when branding loads asynchronously (fixes default view not applying)
  useEffect(() => {
    if (userChangedViewRef.current) return
    const brandingDefault = template.homepage.defaultView as ViewMode
    const views = template.homepage.availableViews as ViewMode[]
    if (!views.includes(brandingDefault)) return
    // Only update if user hasn't saved a preference in localStorage
    const saved = localStorage.getItem('eventViewMode') as ViewMode | null
    if (saved && views.includes(saved)) return
    setViewMode(brandingDefault)
  }, [template.homepage.defaultView, template.homepage.availableViews])

  // If current viewMode is no longer in availableViews, switch to first available
  useEffect(() => {
    if (!availableViews.includes(viewMode)) {
      const fallback = availableViews[0] || 'cards'
      setViewMode(fallback)
      localStorage.setItem('eventViewMode', fallback)
    }
  }, [availableViews, viewMode])

  // When arriving via ?q= or ?location=, switch to appropriate view and clean URL
  useEffect(() => {
    if (initialQuery || initialLocation) {
      if (initialLocation) {
        // Location filter: prefer locations view, fall back to list
        if (availableViews.includes('locations')) {
          setViewMode('locations')
        } else if (availableViews.includes('list')) {
          setViewMode('list')
        }
      } else if (initialQuery) {
        if (availableViews.includes('locations')) {
          setViewMode('locations')
        } else if (availableViews.includes('list')) {
          setViewMode('list')
        }
      }
      // Clean the params from URL without triggering navigation
      router.replace('/', { scroll: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    trackPageView('Events', window.location.href)
  }, [])

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true)
        const params = new URLSearchParams()
        if (activeSearch) params.set('search', activeSearch)
        const url = `/api/events/public${params.toString() ? `?${params}` : ''}`
        const res = await fetch(url)
        const data = await res.json()
        // Also fetch seasons
        const seasonRes = await fetch('/api/seasons/public')
        const seasonData = await seasonRes.json()
        if (seasonData.success) setSeasons(seasonData.seasons || [])

        if (data.success && data.events) {
          const adapted = adaptEventsListForUI(data.events)
          setEvents(adapted)
          trackViewItemList('Homepage Events', adapted.map(e => ({
            id: e.id,
            name: e.name,
            category: 'Event',
          })))
        } else {
          setError(data.error || 'Failed to load events')
        }
      } catch {
        setError('Failed to load events. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    fetchEvents()
  }, [activeSearch])

  const handleViewChange = (mode: ViewMode) => {
    userChangedViewRef.current = true
    setViewMode(mode)
    localStorage.setItem('eventViewMode', mode)
  }

  const uniqueCities = useMemo(() => {
    return [...new Set(events
      .map(e => e.venueCity)
      .filter(Boolean)
    )].sort()
  }, [events])

  const filteredEvents = useMemo(() => {
    if (!locationFilter) return events
    return events.filter(e => e.venueCity === locationFilter)
  }, [events, locationFilter])

  const featuredEvents = useMemo(() => {
    return filteredEvents.filter(e => e.isFeatured)
  }, [filteredEvents])

  const nonFeaturedEvents = useMemo(() => {
    return filteredEvents.filter(e => !e.isFeatured)
  }, [filteredEvents])

  const groupedEvents = useMemo(() => {
    return groupEventsBySeries(nonFeaturedEvents)
  }, [nonFeaturedEvents])

  // Group events by venue for the "Group by Venue" view
  const venueGroups = useMemo(() => {
    if (!groupByVenue) return []
    const map = new Map<string, { venueName: string; venueCity: string; imageUrl: string | null; events: TimedUIEvent[] }>()
    for (const event of filteredEvents) {
      const key = event.venue?.name || 'Other'
      if (!map.has(key)) {
        map.set(key, {
          venueName: key,
          venueCity: event.venueCity || '',
          imageUrl: event.imageUrl || null,
          events: [],
        })
      }
      map.get(key)!.events.push(event)
    }
    // Sort by event count descending
    return Array.from(map.values()).sort((a, b) => b.events.length - a.events.length)
  }, [filteredEvents, groupByVenue])

  const [expandedVenue, setExpandedVenue] = useState<string | null>(null)

  const handleSearchInput = useCallback((value: string) => {
    setSearchTerm(value)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setActiveSearch(value.trim())
    }, 300)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    setActiveSearch(searchTerm.trim())
  }

  // In card view, show grouped count (series collapsed); in other views, show actual count
  const groupedTotal = featuredEvents.length + groupedEvents.series.length + groupedEvents.singles.length
  const totalEvents = viewMode === 'cards' ? groupedTotal : filteredEvents.length
  const uniqueLocations = new Set(filteredEvents.map(e => e.venueCity).filter(Boolean)).size

  // Grid classes based on gridColumns config
  const gridClasses =
    template.homepage.gridColumns.xl === 1 ? 'space-y-2' :
    template.homepage.gridColumns.xl === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' :
    'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'

  // Template-aware hero height
  const heroHeightClass =
    template.homepage.heroHeight === 'compact' ? 'h-[200px]' :
    template.homepage.heroHeight === 'tall' ? 'h-[500px]' : 'h-[350px]'

  const seatedSeriesCalendar = (branding?.layout as Record<string, unknown>)?.seatedSeriesCalendar === true
  const heroTitle = branding?.publicSite?.siteDescription || ''
  const heroSubtitle = branding?.publicSite?.heroSubtitle || ''

  // Interactive hero map from branding context (no API call needed)
  const heroMap = (branding?.assets as Record<string, unknown>)?.heroMap as {
    imageUrl: string
    imageWidth: number
    imageHeight: number
    regions: Array<{ id: string; name: string; shapeType: 'rect' | 'polygon' | 'circle'; x: number; y: number; width: number; height: number; points?: Array<{ x: number; y: number }>; linkedEventId: string | null; linkedEventSlug?: string | null; linkedUrl: string | null; hoverColor: string; borderColor: string; labelColor: string; sortOrder: number; isActive: boolean; alwaysHighlight?: boolean }>
  } | null | undefined
  const heroMapEnabled = (branding?.assets as Record<string, unknown>)?.heroMapEnabled as boolean | undefined

  // Build linked event info for hero map regions from the already-loaded events list
  const heroMapEvents = useMemo(() => {
    if (!heroMap?.regions?.length || events.length === 0) return []
    const eventIds = new Set(
      heroMap.regions.map(r => r.linkedEventId).filter((id): id is string => !!id)
    )
    if (eventIds.size === 0) return []
    return events
      .filter(e => eventIds.has(e.id))
      .map(e => ({
        id: e.id,
        name: e.name,
        slug: e.slug || '',
        status: e.status || 'ON_SALE',
        startDate: e.startDate,
        endDate: e.endDate || undefined,
        imageUrl: e.imageUrl || e.largePic || null,
      }))
  }, [heroMap, events])

  // Inject search bar into the Navbar header on desktop
  const { setSearchContent } = useNavbarExtras()

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    setActiveSearch(searchTerm.trim())
  }, [searchTerm])

  useEffect(() => {
    if (!showEventSearch) {
      setSearchContent(null)
      return
    }
    setSearchContent(
      <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 w-full">
        <div className="relative flex-1">
          <input
            type="search"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => handleSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-surface-secondary border border-surface-border rounded-full text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        {uniqueCities.length > 1 && (
          <div className="relative">
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="appearance-none pl-8 pr-7 py-2 bg-surface-secondary border border-surface-border rounded-full text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer font-medium"
            >
              <option value="">All Locations</option>
              {uniqueCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-text-tertiary pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </form>
    )
    return () => setSearchContent(null)
  }, [searchTerm, locationFilter, uniqueCities, handleSearchSubmit, handleSearchInput, setSearchContent, showEventSearch])

  return (
    <div className="bg-surface-secondary animate-fade-in">
      <main className="max-w-page mx-auto px-4 lg:px-8 py-8">
        {/* Hero Banner */}
        {template.homepage.showHeroBanner && (
          heroMap?.regions?.length && heroMapEnabled !== false ? (
            <div className="relative overflow-hidden rounded-2xl mb-8">
              <VenueMapViewer
                imageUrl={heroMap.imageUrl}
                regions={heroMap.regions}
                events={heroMapEvents}
                className="rounded-2xl"
              />
              {(heroTitle || heroSubtitle) && (
                <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12 pointer-events-none">
                  {heroTitle && (
                    <h1 className="text-3xl lg:text-4xl font-heading font-extrabold mb-1 text-white drop-shadow-lg">
                      {heroTitle}
                    </h1>
                  )}
                  {heroSubtitle && (
                    <p className="text-lg text-white/80 drop-shadow-lg">{heroSubtitle}</p>
                  )}
                </div>
              )}
            </div>
          ) : (
          <div className={`relative overflow-hidden bg-[rgb(var(--color-hero-bg))] rounded-2xl mb-8 ${!branding?.assets?.heroImageUrl ? heroHeightClass : ''}`}>
          {branding?.assets?.heroImageUrl ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={branding.assets.heroImageUrl}
                alt=""
                aria-hidden="true"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              {(heroTitle || heroSubtitle) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              )}
            </>
          ) : (
            <>
              <div className="absolute top-0 right-0 w-64 h-64 bg-text-primary/5 rounded-full -translate-y-1/2 translate-x-1/4" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-text-primary/5 rounded-full translate-y-1/2 -translate-x-1/4" />
              <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-text-primary/[0.03] rounded-full -translate-y-1/2" />
            </>
          )}

          {/* Hero text - anchored to bottom, hidden when blank */}
          {(heroTitle || heroSubtitle) && (
            <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12">
              {heroTitle && (
                <h1 className={`text-3xl lg:text-4xl font-heading font-extrabold mb-1 ${branding?.assets?.heroImageUrl ? 'text-white' : 'text-text-primary'}`}>
                  {heroTitle}
                </h1>
              )}
              {heroSubtitle && (
                <p className={`text-lg ${branding?.assets?.heroImageUrl ? 'text-white/80' : 'text-text-secondary'}`}>{heroSubtitle}</p>
              )}
            </div>
          )}

        </div>
          )
        )}

        {/* Mobile search - below hero */}
        {showEventSearch && (
        <div className="lg:hidden mb-6 event-search" data-search-bar>
          <form onSubmit={handleSearch} className="flex flex-col gap-2.5">
            <div className="relative">
              <input
                type="search"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-surface-secondary border border-surface-border rounded-full text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {uniqueCities.length > 1 && (
              <div className="relative">
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="appearance-none w-full pl-9 pr-8 py-3 bg-surface-secondary border border-surface-border rounded-full text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer font-medium"
                >
                  <option value="">All Locations</option>
                  {uniqueCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            )}
          </form>
        </div>
        )}

        {/* Active filter pills */}
        {(locationFilter || activeSearch) && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-xs text-text-tertiary font-medium">Filters:</span>
            {activeSearch && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-surface-border rounded-full text-xs font-medium text-text-primary shadow-sm">
                Search: {activeSearch}
                <button
                  onClick={() => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); setSearchTerm(''); setActiveSearch('') }}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            {locationFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-surface-border rounded-full text-xs font-medium text-text-primary shadow-sm">
                <svg className="w-3 h-3 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {locationFilter}
                <button
                  onClick={() => setLocationFilter('')}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            <button
              onClick={() => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); setSearchTerm(''); setActiveSearch(''); setLocationFilter('') }}
              className="text-xs text-text-tertiary hover:text-primary transition-colors font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        {loading && <EventCardSkeleton count={8} />}

        {error && (
          <div className="max-w-md mx-auto mt-16">
            <div className="bg-error-light border border-error/20 rounded-xl p-6 text-center">
              <div className="text-error mb-2 font-semibold">Error loading events</div>
              <p className="text-text-secondary text-sm mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filteredEvents.length === 0 && (
          <div className="text-center py-20">
            <svg className="w-14 h-14 mx-auto text-text-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-heading font-semibold text-text-primary mb-1">No events found</h3>
            <p className="text-text-tertiary text-sm">
              {locationFilter ? 'No events in this location. Try a different filter.' : 'Try adjusting your search or check back later'}
            </p>
          </div>
        )}

        {/* showUpcomingEvents gated only the cards/list blocks below, so with it
            off the page still announced "N events available" above an empty
            space — the site contradicted itself and read as broken. The count,
            the view toggle and the listings now stand or fall together. */}
        {!loading && !error && filteredEvents.length > 0 && showUpcomingEvents && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-text-secondary">
                {viewMode === 'locations' && uniqueLocations > 1
                  ? `${uniqueLocations} ${uniqueLocations === 1 ? 'location' : 'locations'}, ${totalEvents} ${totalEvents === 1 ? 'show' : 'shows'}`
                  : `${totalEvents} ${totalEvents === 1 ? 'event' : 'events'} available`}
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={groupByVenue}
                    onChange={e => { setGroupByVenue(e.target.checked); setExpandedVenue(null) }}
                    className="rounded border-surface-border text-primary focus:ring-primary w-4 h-4"
                  />
                  <span className="text-xs font-medium text-text-secondary">Group by Venue</span>
                </label>
                <ViewToggle viewMode={viewMode} onChange={handleViewChange} availableViews={availableViews} />
              </div>
            </div>

            {/* Season Passes */}
            {viewMode === 'cards' && seasons.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-heading font-bold text-text-primary mb-4">Season Passes</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {seasons.map(season => (
                    <a
                      key={season.id}
                      href={`/seasons/${season.slug}`}
                      className="event-card bg-surface border border-surface-border rounded-xl overflow-hidden cursor-pointer group shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-200"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        {season.imageUrl ? (
                          <>
                            <img src={season.imageUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50" />
                            <img src={season.imageUrl} alt={season.name} className="relative z-10 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                            <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                          </>
                        ) : (
                          <div className="w-full h-full bg-surface-tertiary flex items-center justify-center">
                            <svg className="w-12 h-12 text-text-tertiary opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                          </div>
                        )}
                        <div className="absolute top-3 left-3 z-30 px-2.5 py-1 bg-black/80 backdrop-blur-sm text-white text-[11px] font-semibold rounded-md shadow-md">
                          Season Pass
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-bold text-text-primary group-hover:text-primary transition-colors line-clamp-2">
                          {season.name}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs text-text-secondary mt-1.5">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                          {season.venue.name}
                        </div>
                        <div className="text-xs text-text-tertiary mt-1">
                          {season.eventCount} events included
                        </div>
                        {season.startingAt != null && (
                          <div className="mt-3 pt-3 border-t border-surface-border">
                            <span className="text-sm font-bold text-text-primary">
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(season.startingAt)}
                            </span>
                            <span className="text-xs text-text-tertiary ml-1">starting</span>
                          </div>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Venue-grouped view */}
            {groupByVenue && (viewMode === 'cards' || viewMode === 'list') && (
              <div className="space-y-4">
                {venueGroups.map(group => {
                  const isExpanded = expandedVenue === group.venueName
                  return (
                    <div key={group.venueName} className="bg-surface border border-surface-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedVenue(isExpanded ? null : group.venueName)}
                        className="w-full flex items-center gap-4 p-4 hover:bg-surface-secondary/50 transition-colors text-left"
                      >
                        {/* Venue image */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-surface-secondary">
                          {group.imageUrl ? (
                            <img src={group.imageUrl} alt={group.venueName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-text-primary truncate">{group.venueName}</h3>
                          {group.venueCity && (
                            <p className="text-xs text-text-secondary mt-0.5">{group.venueCity}</p>
                          )}
                          <p className="text-xs text-text-tertiary mt-0.5">
                            {group.events.length} event{group.events.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <svg className={`w-5 h-5 text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-surface-border p-4">
                          {viewMode === 'cards' ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {group.events.map(event => (
                                <EventCard key={event.id} event={event} />
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {group.events.map(event => (
                                <EventListItem key={event.id} event={event} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {!groupByVenue && viewMode === 'cards' && featuredEvents.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-heading font-bold text-text-primary mb-4">Featured Events</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {featuredEvents.map((event) => (
                    <EventCard key={event.id} event={event as TimedUIEvent} />
                  ))}
                </div>
              </div>
            )}

            {!groupByVenue && viewMode === 'cards' && showUpcomingEvents && (
              <div className={gridClasses}>
                {groupedEvents.series.map((series) => (
                  <SeriesCard key={series.id} series={series as never} seatedSeriesCalendar={seatedSeriesCalendar} />
                ))}
                {groupedEvents.singles.map((event) => (
                  <EventCard key={event.id} event={event as TimedUIEvent} />
                ))}
              </div>
            )}

            {!groupByVenue && viewMode === 'list' && showUpcomingEvents && (
              <div className="space-y-2">
                {featuredEvents.length > 0 && (
                  <>
                    <h2 className="text-lg font-heading font-bold text-text-primary mb-2">Featured Events</h2>
                    {featuredEvents.map((event) => (
                      <EventListItem key={event.id} event={event as TimedUIEvent} />
                    ))}
                    {(groupedEvents.series.length > 0 || groupedEvents.singles.length > 0) && (
                      <div className="pt-4" />
                    )}
                  </>
                )}
                {groupedEvents.series.map((series) => (
                  <SeriesListItem key={series.id} series={series as never} seatedSeriesCalendar={seatedSeriesCalendar} />
                ))}
                {groupedEvents.singles.map((event) => (
                  <EventListItem key={event.id} event={event as TimedUIEvent} />
                ))}
              </div>
            )}

            {viewMode === 'locations' && (
              <LocationGroupView
                events={filteredEvents}
                clickBehavior={
                  (branding?.layout as { locationsClickBehavior?: 'expand' | 'page' } | undefined)
                    ?.locationsClickBehavior === 'page'
                    ? 'page'
                    : 'expand'
                }
              />
            )}

            {viewMode === 'map' && (
              <EventMap
                events={filteredEvents as TimedUIEvent[]}
                onViewEvents={(city) => {
                  setLocationFilter(city)
                  const target = availableViews.includes('locations')
                    ? 'locations'
                    : availableViews.includes('cards')
                      ? 'cards'
                      : availableViews.includes('list')
                        ? 'list'
                        : viewMode
                  setViewMode(target)
                  localStorage.setItem('eventViewMode', target)
                }}
              />
            )}
          </>
        )}
      </main>
    </div>
  )
}
